import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SpaceBetween,
  PropertyFilter,
  Table,
  Link,
  ButtonDropdown,
  Header,
  StatusIndicator,
  Spinner,
  Button,
  Icon,
} from '@cloudscape-design/components';
import { PipelinesTableProps, TimeframePeriod, PipelineCost } from '../../types';
import { filteringProperties } from '../../data/sampleData';
import { apiService } from '../../services/apiService';
import { useUser } from '../../contexts/UserContext';
import { PropertyFilterProps } from '@cloudscape-design/components';

const PipelinesTable: React.FC<PipelinesTableProps> = ({
  items,
  selectedItems,
  onSelectionChange,
  filteringQuery,
  onFilteringChange,
  loading = false,
  timeframe = 'weekly',
  onRefresh,
}) => {
  const { session } = useUser();
  const [costData, setCostData] = useState<Map<string, PipelineCost>>(new Map());
  const [costLoading, setCostLoading] = useState<Set<string>>(new Set());

  // Helper: extract account ID from SNS ARN (arn:aws:sns:region:ACCOUNT_ID:topic)
  const getAccountIdFromArns = (arns: string[]): string | null => {
    if (!arns || arns.length === 0) return null;
    const parts = arns[0].split(':');
    return parts.length >= 5 ? parts[4] : null;
  };

  // Generate dynamic filtering options based on current pipeline data
  const filteringOptions: PropertyFilterProps.FilteringOption[] = useMemo(() => {
    const options: PropertyFilterProps.FilteringOption[] = [];

    // Add pipeline name suggestions
    items.forEach((item) => {
      if (item.name) {
        options.push({
          label: item.name,
          propertyKey: 'name',
          value: item.name,
        });
      }
    });

    // Add tag suggestions from all items
    const allTags = new Set<string>();
    items.forEach((item) => {
      if (item.tags) {
        item.tags.forEach((tag) => allTags.add(tag));
      }
    });

    allTags.forEach((tag) => {
      options.push({
        label: tag,
        propertyKey: 'tags',
        value: tag,
      });
    });

    // Add SNS source topic name suggestions
    const allSnsTopics = new Set<string>();
    items.forEach((item: any) => {
      const arns: string[] = item.sns_sources || [];
      arns.forEach((arn: string) => {
        const topicName = arn.split(':').pop();
        if (topicName) allSnsTopics.add(topicName);
      });
    });

    allSnsTopics.forEach((topic) => {
      options.push({
        label: topic,
        propertyKey: 'sns_source_name',
        value: topic,
      });
    });

    // Add destination table suggestions
    const allDestTables = new Set<string>();
    items.forEach((item: any) => {
      const tables: string[] = item.destination_tables || [];
      tables.forEach((t: string) => {
        if (t) allDestTables.add(t);
      });
    });

    allDestTables.forEach((table) => {
      options.push({
        label: table,
        propertyKey: 'destination_table_name',
        value: table,
      });
    });

    return options;
  }, [items]);

  // Enrich items with sns_source_name for PropertyFilter matching
  const enrichedItems = useMemo(() => {
    return items.map((item: any) => {
      const arns: string[] = item.sns_sources || [];
      const topicNames = arns.map((arn: string) => arn.split(':').pop() || '').join(', ');
      const tables: string[] = item.destination_tables || [];
      const tableNames = tables.join(', ');
      return { ...item, sns_source_name: topicNames, destination_table_name: tableNames };
    });
  }, [items]);

  // Fetch cost data for a specific pipeline
  const fetchPipelineCost = useCallback(
    async (pipelineName: string) => {
      if (!session) return;

      setCostLoading((prev) => new Set(prev).add(pipelineName));

      try {
        const cost = await apiService.getPipelineCost(pipelineName, timeframe, session);
        setCostData((prev) => new Map(prev).set(pipelineName, cost));
      } catch (error) {
        console.error(`Error fetching cost for ${pipelineName}:`, error);
        setCostData((prev) =>
          new Map(prev).set(pipelineName, {
            weekly_cost: 0,
            monthly_cost: 0,
            annual_cost: 0,
            currency: 'USD',
            error: 'Failed to load cost data',
          }),
        );
      } finally {
        setCostLoading((prev) => {
          const newSet = new Set(prev);
          newSet.delete(pipelineName);
          return newSet;
        });
      }
    },
    [session, timeframe],
  );

  // Create a stable list of pipeline identifiers to avoid re-fetching on data updates
  const pipelineIdentifiers = useMemo(() => {
    return items
      .filter((pipeline) => pipeline.status !== 'disabled')
      .map((pipeline) => pipeline.filename || pipeline.name);
  }, [items.map((p) => `${p.filename || p.name}-${p.status}`).join(',')]);

  // Fetch costs only when pipeline identifiers or timeframe changes
  useEffect(() => {
    if (pipelineIdentifiers.length === 0 || !session) return;

    // Clear existing cost data when timeframe changes
    setCostData(new Map());

    // Fetch costs for all enabled pipelines
    pipelineIdentifiers.forEach((pipelineIdentifier: string) => {
      fetchPipelineCost(pipelineIdentifier);
    });
  }, [pipelineIdentifiers, timeframe, fetchPipelineCost, session]);

  // Format cost display
  const formatCost = (cost: PipelineCost | undefined, timeframe: TimeframePeriod) => {
    if (!cost) return '-';

    if (cost.error) {
      return <span style={{ color: '#d13212' }}>Error</span>;
    }

    let amount = 0;

    switch (timeframe) {
      case 'daily':
        amount = cost.weekly_cost / 7;
        break;
      case 'weekly':
        amount = cost.weekly_cost;
        break;
      case 'monthly':
        amount = cost.monthly_cost;
        break;
      case 'yearly':
        amount = cost.annual_cost;
        break;
    }

    if (amount === 0) return '$0.00';

    return `$${amount.toFixed(2)}`;
  };
  const handleFindInCode = () => {
    selectedItems.forEach((pipeline) => {
      if (pipeline.filename) {
        const codeUrl = `https://code.amazon.com/packages/DTPCatalyst/blobs/heads/mainline/--/src/configs/feeds/${pipeline.filename}.yaml`;
        window.open(codeUrl, '_blank');
      } else {
        console.warn(`No filename available for pipeline: ${pipeline.name}`);
      }
    });
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'healthy':
        return <StatusIndicator type="success">Healthy</StatusIndicator>;
      case 'in-alarm':
        return <StatusIndicator type="warning">In Alarm</StatusIndicator>;
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>;
      case 'disabled':
        return <StatusIndicator type="stopped">Disabled</StatusIndicator>;
      default:
        return <StatusIndicator type="info">{status}</StatusIndicator>;
    }
  };

  const columnDefinitions = [
    {
      cell: (item: any) => (
        <Link href={`#/pipelines/${item.filename || item.name}`} variant="primary">
          {item.name}
        </Link>
      ),
      header: 'Pipeline name',
      id: 'name',
    },
    {
      cell: (item: any) => getStatusIndicator(item.status),
      header: 'Status',
      id: 'status',
    },
    {
      cell: (item: any) => {
        const pipelineIdentifier = item.filename || item.name;
        const cost = costData.get(pipelineIdentifier);
        const isLoading = costLoading.has(pipelineIdentifier);

        if (item.status === 'disabled') {
          return <span style={{ color: '#5f6b7a' }}>-</span>;
        }

        if (isLoading) {
          return <Spinner />;
        }

        // Use real data from API if available, fallback to sample data
        const recordsOut = cost?.records_written ?? item.recordsOut ?? 0;
        return <>{recordsOut.toLocaleString()}</>;
      },
      header: 'Records out',
      id: 'records',
    },
    {
      cell: (item: any) => {
        const pipelineIdentifier = item.filename || item.name;
        const cost = costData.get(pipelineIdentifier);
        const isLoading = costLoading.has(pipelineIdentifier);

        if (item.status === 'disabled') {
          return <span style={{ color: '#5f6b7a' }}>-</span>;
        }

        if (isLoading) {
          return <Spinner />;
        }

        // Use real data from API if available, fallback to sample data
        const tps = cost?.records_per_second ?? item.tps ?? 0;
        return <>{tps.toLocaleString()}</>;
      },
      header: 'TPS',
      id: 'tps',
    },
    // {
    //   cell: (item: any) => <>-</>,
    //   header: 'Latency (avg/min/max)',
    //   id: 'latency',
    // },
    {
      cell: (item: any) => {
        const pipelineIdentifier = item.filename || item.name;
        const cost = costData.get(pipelineIdentifier);
        const isLoading = costLoading.has(pipelineIdentifier);

        if (item.status === 'disabled') {
          return <span style={{ color: '#5f6b7a' }}>Disabled</span>;
        }

        if (isLoading) {
          return <Spinner />;
        }

        return formatCost(cost, timeframe);
      },
      header: 'IMR Cost',
      id: 'cost',
    },
    {
      cell: (item: any) => {
        const arns: string[] = item.sns_sources || [];
        if (arns.length === 0) return '-';
        const topicNames = arns.map((arn: string) => arn.split(':').pop()).join(', ');
        const fullArns = arns.join('\n');
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            <span title={fullArns}>{topicNames}</span>
            <span
              style={{ cursor: 'pointer', opacity: 0.6 }}
              title="Copy full ARN"
              onClick={() => {
                navigator.clipboard.writeText(arns.join(', '));
              }}
            >
              <Icon name="copy" />
            </span>
          </SpaceBetween>
        );
      },
      header: 'Source SNS',
      id: 'sourceSns',
    },
    {
      cell: (item: any) => {
        const tables: string[] = item.destination_tables || [];
        if (tables.length === 0) return '-';
        const display =
          tables.length <= 2 ? tables.join(', ') : `${tables[0]}, +${tables.length - 1} more`;
        return (
          <span
            title={tables.join('\n')}
            style={{
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {display}
          </span>
        );
      },
      header: 'Destination tables',
      id: 'destinationTable',
    },
    {
      cell: (item: any) => {
        const arns: string[] = item.sns_sources || [];
        const accountId = getAccountIdFromArns(arns);
        if (!accountId) return '-';
        return (
          <Link
            href={`https://api.conduit.merlon.amazon.dev/api/accounts/partition/aws/accountId/${accountId}?fetchFromDynamoDB=true`}
            external
          >
            CTI
          </Link>
        );
      },
      header: 'Source CTI',
      id: 'sourceCti',
    },
  ];

  return (
    <SpaceBetween direction="vertical" size="m">
      <PropertyFilter
        countText={`${items.length} pipelines`}
        filteringOptions={filteringOptions}
        filteringPlaceholder="Find pipelines by name, tags, or SNS source"
        filteringProperties={filteringProperties}
        query={filteringQuery}
        onChange={({ detail }) => onFilteringChange(detail)}
      />
      <Table
        columnDefinitions={columnDefinitions}
        items={enrichedItems}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
        selectionType="multi"
        stickyHeader
        stripedRows
        variant="full-page"
        trackBy={(item) => `${item.filename || item.name}-${item.id}`}
        loading={loading}
        loadingText="Loading pipelines..."
        data-venue="true"
        header={
          <Header
            variant="h2"
            counter={`(${items.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" variant="normal" onClick={onRefresh} loading={loading}>
                  Refresh
                </Button>
                <ButtonDropdown
                  items={[
                    {
                      text: 'Find in code',
                      id: 'find-in-code',
                      disabled: selectedItems.length === 0,
                    },
                  ]}
                  onItemClick={() => handleFindInCode()}
                  disabled={selectedItems.length === 0}
                >
                  Actions
                </ButtonDropdown>
              </SpaceBetween>
            }
          >
            Pipelines
          </Header>
        }
      />
    </SpaceBetween>
  );
};

export default PipelinesTable;
